import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

// eslint-disable-next-line no-restricted-imports, monorepo/no-internal-import
import { useInViewEffect } from '@aboutyou/ui-components/src/hooks/useInView/useInViewEffect'
import Carousel from 'components/Carousel'
import HeroSliderArrow from './HeroSliderArrow/HeroSliderArrow'
import HeroSliderPagination from './HeroSliderPagination/HeroSliderPagination'

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

const fillBrowseViewportHeight = css`
  height: 65vh;
  height: calc(100vh - var(--layout-header-show-more-height, 10vh));
  min-height: 375px;
`

const FadeInContainer = styled.div`
  ${fillBrowseViewportHeight};
  position: relative;
  animation: ${fadeIn} 1500ms;
`

const StyledCarousel = styled(Carousel)`
  height: 100%;
`

interface Props {
  className?: string
}

const HeroSlider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
}) => {
  const carouselRef = useRef<Carousel>(null)
  const isInViewport = useRef(false)
  const [isVisible, setVisible] = useState(false)

  const onVisibilityChange = useCallback(() => {
    const newIsVisibleState =
      isInViewport && document.visibilityState === 'visible'
    setVisible(newIsVisibleState)
  }, [isInViewport, setVisible])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }
    }
  }, [onVisibilityChange])

  useEffect(() => {
    if (isVisible) {
      carouselRef?.current?.playSlick()
    } else {
      carouselRef?.current?.pauseSlick()
    }
  }, [isVisible])

  const { ref } = useInViewEffect<HTMLDivElement>(
    inView => {
      isInViewport.current = inView
      setVisible(inView && document.visibilityState === 'visible')
    },
    {
      threshold: 0.01,
    },
  )

  return (
    <div className={className} ref={ref}>
      <FadeInContainer>
        {React.Children.count(children) > 0 && (
          <StyledCarousel
            withCustomDots
            infinite
            touchMove
            autoplay={isVisible}
            ref={carouselRef}
            autoplaySpeed={20000}
            Pagination={HeroSliderPagination as any}
            PrevArrow={HeroSliderArrow as any}
            NextArrow={HeroSliderArrow as any}
          >
            {children}
          </StyledCarousel>
        )}
      </FadeInContainer>
    </div>
  )
}

export default HeroSlider
